/**
 * D19 — Donation Receipt.
 *
 * Uses a back arrow rather than the hamburger the artwork showed (gap G-09):
 * the receipt is always reached by drilling into a donation.
 *
 * The rows come from `receiptRows` in `@mfa/domain`, so the phone receipt, a
 * PDF and the admin view cannot list different fields in a different order.
 */

import { StyleSheet, View } from 'react-native';

import { formatDate, formatDateTime, formatMoney, receiptRows } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card, CardDivider, CardRow } from '../../components/common/Card';
import { CheckIcon, Glyph } from '../../components/common/Icon';
import { ErrorState, LoadingState } from '../../components/feedback/States';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import { useDonation } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';

export function ReceiptScreen({ navigation, route }: DonorScreenProps<'Receipt'>) {
  const t = useT();
  const { donationId } = route.params;
  const query = useDonation(donationId);

  if (query.isLoading) {
    return (
      <ScreenShell leading="back" onBackPress={navigation.goBack} testID="screen-receipt">
        <LoadingState label={t('Loading receipt')} />
      </ScreenShell>
    );
  }

  if (query.isError || !query.data) {
    return (
      <ScreenShell leading="back" onBackPress={navigation.goBack} testID="screen-receipt">
        <ErrorState title={t('Receipt unavailable')} onRetry={() => void query.refetch()} />
      </ScreenShell>
    );
  }

  const donation = query.data;
  const rows = receiptRows(donation, {
    money: formatMoney,
    date: formatDate,
    dateTime: formatDateTime,
  });

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-receipt"
      pinnedFooter={
        <Button
          label={t('Back to Home')}
          onPress={() => navigation.navigate('Home')}
          leadingIcon={<Glyph name="home" size={18} />}
          testID="receipt-cta-home"
        />
      }
    >
      <View style={styles.hero}>
        <CheckIcon size={72} />
        <Text variant="screenTitle" tone="brand" align="center" style={styles.title}>{t('Thank You!')}</Text>
        <Text variant="body" tone="secondary" align="center">{t('Your donation receipt')}</Text>
      </View>

      <Card testID="receipt-rows">
        {rows.map((row, index) => (
          <View key={row.label}>
            {index > 0 ? <CardDivider /> : null}
            <CardRow label={row.label} value={row.value} />
          </View>
        ))}
      </Card>

      <View style={styles.actions}>
        <Button
          label={t('Download')}
          variant="secondary"
          leadingIcon={<Glyph name="download" size={16} />}
          onPress={() => {}}
          style={styles.action}
          testID="cta-download"
        />
        <Button
          label={t('Share')}
          variant="secondary"
          leadingIcon={<Glyph name="share" size={16} />}
          onPress={() => {}}
          style={styles.action}
          testID="cta-share"
        />
      </View>

      <View style={styles.note}>
        <Glyph name="privacy" size={16} />
        <Text variant="caption" tone="tertiary" style={styles.noteText}>
          This receipt shows only the reference your provider issued. Your card details are
          never stored.
        </Text>
      </View>
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  hero: {
    alignItems: 'center',
    gap: space.xs,
    paddingVertical: space.lg,
  },
  title: {
    marginTop: space.sm,
  },
  actions: {
    flexDirection: 'row',
    gap: space.md,
    marginTop: space.lg,
  },
  action: {
    flex: 1,
  },
  note: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    marginTop: space.lg,
    padding: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.bg.surfaceSunken,
  },
  noteText: {
    flex: 1,
  },
});
